import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { PropTypes } from '../../../utils/propTypes';
import PopoverPrimitive from '../Popover/Popover';
import styles from './PopoverMenu.module.css';

/**
 * Popover (compound) — a shadcn-style trigger/content popover, reimplemented in
 * Athena's idiom (JSX + CSS Modules + 2.0 tokens). No Radix/Base-UI dependency:
 * the panel renders through the existing imperative <Popover> primitive
 * (portal / positioning / flip / click-outside / Escape).
 *
 * Distinct from `components/ui/Popover` (the imperative primitive) — this is the
 * self-contained compound family that owns its own open state and uses the
 * trigger as the anchor. Lives in its own folder to avoid the `Popover` name
 * clash; styled with `--popover-content-*` tokens (globals.css).
 *
 *   <Popover>
 *     <PopoverTrigger asChild><Button>Open</Button></PopoverTrigger>
 *     <PopoverContent>
 *       <PopoverHeader>
 *         <PopoverTitle>…</PopoverTitle>
 *         <PopoverDescription>…</PopoverDescription>
 *       </PopoverHeader>
 *     </PopoverContent>
 *   </Popover>
 */

const PopoverContext = createContext(null);
const usePopover = () => {
  const ctx = useContext(PopoverContext);
  if (!ctx) throw new Error('Popover sub-components must be used within <Popover>');
  return ctx;
};

const mergeRefs = (...refs) => (node) => {
  refs.forEach((ref) => {
    if (!ref) return;
    if (typeof ref === 'function') ref(node);
    else ref.current = node;
  });
};

/* shadcn side+align → the imperative primitive's placement vocabulary
   ('bottom-start' | 'bottom-end' | 'top-start' | 'top-end' | 'right-start' |
   'left-start'). Side placements (right/left) are top-aligned — used by
   cascading submenus. */
const toPlacement = (side, align) => {
  if (side === 'right') return 'right-start';
  if (side === 'left') return 'left-start';
  const v = side === 'top' ? 'top' : 'bottom';
  const a = align === 'center' ? 'center' : align === 'end' ? 'end' : 'start';
  return `${v}-${a}`;
};

const Popover = ({ open: controlledOpen, defaultOpen = false, onOpenChange, children }) => {
  const isControlled = controlledOpen !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const triggerRef = useRef(null);

  const setOpen = useCallback(
    (next) => {
      const value = typeof next === 'function' ? next(open) : next;
      if (!isControlled) setUncontrolledOpen(value);
      onOpenChange?.(value);
    },
    [isControlled, onOpenChange, open]
  );

  const ctx = useMemo(() => ({ open, setOpen, triggerRef }), [open, setOpen]);
  return <PopoverContext.Provider value={ctx}>{children}</PopoverContext.Provider>;
};

/* Trigger supports three forms:
 *   - render={<Button/>}  (base-ui docs): renders that element, the trigger's
 *     children become its content
 *   - asChild + a single child element (Radix-style alias): merges onto the child
 *   - neither: renders its own <button>
 * All merge ref + onClick + aria-haspopup/expanded onto the resolved element. */
const PopoverTrigger = ({ asChild = false, render, children, className = '', onClick, ...props }) => {
  const { open, setOpen, triggerRef } = usePopover();
  const handleClick = (ownOnClick) => (e) => {
    ownOnClick?.(e);
    onClick?.(e);
    setOpen((o) => !o);
  };
  const sharedProps = (el, content) =>
    React.cloneElement(
      el,
      {
        ...props,
        ref: mergeRefs(triggerRef, el.ref),
        className: [el.props.className, className].filter(Boolean).join(' ') || undefined,
        onClick: handleClick(el.props.onClick),
        'aria-haspopup': 'dialog',
        'aria-expanded': open,
      },
      content
    );

  if (render && React.isValidElement(render)) {
    return sharedProps(render, children);
  }
  if (asChild && React.isValidElement(children)) {
    return sharedProps(children, children.props.children);
  }

  return (
    <button
      ref={triggerRef}
      type="button"
      className={className}
      onClick={handleClick()}
      aria-haspopup="dialog"
      aria-expanded={open}
      {...props}
    >
      {children}
    </button>
  );
};

const PopoverContent = ({
  children,
  className = '',
  side = 'bottom',
  align = 'center',
  sideOffset = 4,
  bare = false,
  open: openProp,
  triggerRef: triggerRefProp,
  onClose: onCloseProp,
  minWidth,
  panelClassName = '',
}) => {
  // Works two ways: inside a <Popover> root (reads open/triggerRef from
  // context — the normal shadcn usage), OR standalone via explicit
  // open/triggerRef/onClose props for consumers that own their own state
  // (e.g. Select). `bare` skips the dialog content chrome (padding/width/gap)
  // so such consumers can supply their own inner layout on the 2.0 panel.
  const ctx = useContext(PopoverContext);
  const open = openProp !== undefined ? openProp : ctx?.open;
  const triggerRef = triggerRefProp || ctx?.triggerRef;
  const handleClose = onCloseProp || (() => ctx?.setOpen(false));

  return (
    <PopoverPrimitive
      isOpen={open}
      triggerRef={triggerRef}
      onClose={handleClose}
      placement={toPlacement(side, align)}
      offset={sideOffset}
      minWidth={minWidth}
      className={`${styles.popoverReset} ${panelClassName}`.trim()}
    >
      {bare ? (
        children
      ) : (
        <div className={`${styles.content} ${className}`} role="dialog">
          {children}
        </div>
      )}
    </PopoverPrimitive>
  );
};

const PopoverHeader = ({ children, className = '' }) => (
  <div className={`${styles.header} ${className}`}>{children}</div>
);

const PopoverTitle = ({ children, className = '' }) => (
  <h4 className={`${styles.title} ${className}`}>{children}</h4>
);

const PopoverDescription = ({ children, className = '' }) => (
  <p className={`${styles.description} ${className}`}>{children}</p>
);

Popover.propTypes = {
  open: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  onOpenChange: PropTypes.func,
  children: PropTypes.node,
};
PopoverTrigger.propTypes = {
  asChild: PropTypes.bool,
  render: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string,
  onClick: PropTypes.func,
};
PopoverContent.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  side: PropTypes.string,
  align: PropTypes.string,
  sideOffset: PropTypes.number,
  bare: PropTypes.bool,
  open: PropTypes.bool,
  triggerRef: PropTypes.object,
  onClose: PropTypes.func,
  minWidth: PropTypes.number,
  panelClassName: PropTypes.string,
};
PopoverHeader.propTypes = { children: PropTypes.node, className: PropTypes.string };
PopoverTitle.propTypes = { children: PropTypes.node, className: PropTypes.string };
PopoverDescription.propTypes = { children: PropTypes.node, className: PropTypes.string };

export {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverTitle,
  PopoverDescription,
};
